import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface Excursion {
  id: string;
  title: string;
  category: string;
  tags?: {
    coup_de_coeur?: boolean;
    impact_solidaire?: boolean;
  };
  resume?: string;
  description_longue?: string;
  pourquoi_choisir?: string[];
  debut?: string;
  duree?: string;
  prix_eur: number;
  participants_min: number;
  participants_max: number;
  niveau: number;
  cover_image?: string;
  images?: string[];
  image_captions?: string[];
  itineraire?: Array<{
    heure: string;
    titre: string;
    description: string;
  }>;
  inclus?: string[];
  non_inclus?: string[];
  equipement_recommande?: string[];
  points_forts?: string[];
  bon_a_savoir?: string;
  faq?: Array<{
    question: string;
    reponse: string;
  }>;
  ordre?: number;
  actif?: boolean;
  is_published?: boolean;
  whatsapp_catalog_id?: string;
  created_at?: string;
  updated_at?: string;
}

export const useExcursions = (adminMode = false) => {
  const [excursions, setExcursions] = useState<Excursion[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchExcursions = async () => {
    try {
      let query = supabase
        .from('excursions')
        .select('*')
        .order('ordre', { ascending: true });

      if (!adminMode) {
        query = query.eq('is_published', true);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Transform the data to match our Excursion type
      const transformedData = (data || []).map(item => ({
        ...item,
        tags: item.tags as { coup_de_coeur?: boolean; impact_solidaire?: boolean; } || {},
        image_captions: item.image_captions as string[] || [],
        itineraire: item.itineraire as Array<{ heure: string; titre: string; description: string; }> || [],
        faq: item.faq as Array<{ question: string; reponse: string; }> || []
      }));

      setExcursions(transformedData);
    } catch (error) {
      console.error('Error fetching excursions:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les excursions.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const addExcursion = async (excursionData: Omit<Excursion, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('excursions')
        .insert([excursionData])
        .select();

      if (error) throw error;

      if (data && data[0]) {
        const transformedItem = {
          ...data[0],
          tags: data[0].tags as { coup_de_coeur?: boolean; impact_solidaire?: boolean; } || {},
          image_captions: data[0].image_captions as string[] || [],
          itineraire: data[0].itineraire as Array<{ heure: string; titre: string; description: string; }> || [],
          faq: data[0].faq as Array<{ question: string; reponse: string; }> || []
        };
        setExcursions(prev => [...prev, transformedItem]);
        
        // Force a complete refresh to ensure consistency
        setTimeout(() => {
          fetchExcursions();
        }, 100);
        
        toast({
          title: "Succès",
          description: "Excursion ajoutée avec succès."
        });
        return transformedItem;
      }
    } catch (error) {
      console.error('Error adding excursion:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'ajouter l'excursion.",
        variant: "destructive"
      });
      throw error;
    }
  };

  const updateExcursion = async (id: string, updates: Partial<Excursion>) => {
    try {
      const { data, error } = await supabase
        .from('excursions')
        .update(updates)
        .eq('id', id)
        .select();

      if (error) throw error;

      if (data && data[0]) {
        const transformedItem = {
          ...data[0],
          tags: data[0].tags as { coup_de_coeur?: boolean; impact_solidaire?: boolean; } || {},
          image_captions: data[0].image_captions as string[] || [],
          itineraire: data[0].itineraire as Array<{ heure: string; titre: string; description: string; }> || [],
          faq: data[0].faq as Array<{ question: string; reponse: string; }> || []
        };
        setExcursions(prev => prev.map(excursion => 
          excursion.id === id ? transformedItem : excursion
        ));
        
        // Force a complete refresh to ensure consistency across all contexts
        setTimeout(() => {
          fetchExcursions();
        }, 100);
        
        toast({
          title: "Succès",
          description: "Excursion mise à jour avec succès."
        });
        return transformedItem;
      }
    } catch (error) {
      console.error('Error updating excursion:', error);
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour l'excursion.",
        variant: "destructive"
      });
      throw error;
    }
  };

  const deleteExcursion = async (id: string) => {
    try {
      const { error } = await supabase
        .from('excursions')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setExcursions(prev => prev.filter(excursion => excursion.id !== id));
      
      // Force a complete refresh to ensure consistency
      setTimeout(() => {
        fetchExcursions();
      }, 100);
      
      toast({
        title: "Succès",
        description: "Excursion supprimée avec succès."
      });
    } catch (error) {
      console.error('Error deleting excursion:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer l'excursion.",
        variant: "destructive"
      });
      throw error;
    }
  };

  const togglePublished = async (id: string, is_published: boolean) => {
    await updateExcursion(id, { is_published });
  };

  const getExcursionById = async (id: string): Promise<Excursion | null> => {
    try {
      const { data, error } = await supabase
        .from('excursions')
        .select('*')
        .eq('id', id)
        .eq('is_published', true)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return data ? {
        ...data,
        tags: data.tags as { coup_de_coeur?: boolean; impact_solidaire?: boolean; } || {},
        image_captions: data.image_captions as string[] || [],
        itineraire: data.itineraire as Array<{ heure: string; titre: string; description: string; }> || [],
        faq: data.faq as Array<{ question: string; reponse: string; }> || []
      } : null;
    } catch (error) {
      console.error('Error fetching excursion by id:', error);
      return null;
    }
  };

  const refetch = () => {
    setLoading(true);
    fetchExcursions();
  };

  useEffect(() => {
    fetchExcursions();
  }, [adminMode]);

  return {
    excursions,
    loading,
    addExcursion,
    updateExcursion,
    deleteExcursion,
    togglePublished,
    getExcursionById,
    refetch
  };
};